import { Button, Grid, Group, Stack, Table, Text, Title } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import Dates from 'components/Dates';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { CompetencyModuleDetail } from 'types/dacum/moduleManagement';
import { useModuleManagementStore } from 'store/moduleManagement';
import Card from 'components/Card';

interface DetailModuleSectionProps {
  data: CompetencyModuleDetail;
}

const DetailModuleSection: React.FC<DetailModuleSectionProps> = ({ data }) => {
  const navigate = useNavigate();
  const { setMode, setFormData } = useModuleManagementStore();

  const handleEdit = () => {
    setMode('edit');
    setFormData({
      trCompetencyModuleID: data.trCompetencyModuleID,
      msCompetencyManagementID: data.msCompetencyManagementID,
      msLCMSCourseID: data.msLCMSCourseID,
      deliveryMode: data.deliveryMode,
      liaType: data.liaType,
      courseName: data.courseName,
      dateIn: data.dateIn,
      userIn: data.userIn,
      dateUp: data.dateUp,
      userUp: data.userUp
    });
    navigate(`${pathnames.editCompetencyModule.replace(':id', data.trCompetencyModuleID)}`);
  };

  return (
    <Stack gap={24}>
      {/* Module Information */}
      <Card withBorder>
        <Stack gap={16}>
          <Group justify='space-between'>
            <Title order={3}>Module Information</Title>
            <Button leftSection={<IconEdit size={16} />} onClick={handleEdit}>
              Edit Module
            </Button>
          </Group>

          <Grid>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Course Name
                </Text>
                <Text>{data.courseName}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Delivery Mode
                </Text>
                <Text>{data.deliveryMode}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  LIA Type
                </Text>
                <Text>{data.liaType}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Module ID
                </Text>
                <Text>{data.trCompetencyModuleID}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Card>

      {/* Job Families */}
      <Card withBorder>
        <Stack gap={16}>
          <Title order={3}>Associated Job Families</Title>

          {data.jobFamilies && data.jobFamilies.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Job Family ID</Table.Th>
                  <Table.Th>Job Family Name</Table.Th>
                  <Table.Th>Created Date</Table.Th>
                  <Table.Th>Created By</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.jobFamilies.map((jobFamily) => (
                  <Table.Tr key={jobFamily.trCompetencyModuleJobFamilyID}>
                    <Table.Td>{jobFamily.jobFamilyID}</Table.Td>
                    <Table.Td>{jobFamily.jobFamilyName || '-'}</Table.Td>
                    <Table.Td>
                      <Dates date={jobFamily.dateIn} />
                    </Table.Td>
                    <Table.Td>{jobFamily.userIn || '-'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c='dimmed' ta='center' py={20}>
              No job families associated with this module
            </Text>
          )}
        </Stack>
      </Card>

      {/* Audit Information */}
      <Card withBorder>
        <Stack gap={16}>
          <Title order={3}>Audit Information</Title>

          <Grid>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Created Date
                </Text>
                <Text>
                  <Dates date={data.dateIn} />
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Created By
                </Text>
                <Text>{data.userIn}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Updated Date
                </Text>
                <Text>{data.dateUp ? <Dates date={data.dateUp} /> : '-'}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap={8}>
                <Text size='sm' fw={500} c='dimmed'>
                  Updated By
                </Text>
                <Text>{data.userUp || '-'}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
};

export default DetailModuleSection;
